import type { components } from "../../api/generated-schema";
import {
  ApiContractError,
  ApiProblem,
  apiRequest,
  apiRequestWithMeta,
  createRequestId,
} from "../../api/httpClient";
import type { ProblemDetails } from "../../api/httpClient";
import { getAuthenticationSession } from "../../auth/session";

/**
 * API module typed cho mộ và liên kết mộ–liệt sĩ (SCR-036/SCR-037 — S11.6, khuôn
 * `nccProfileApi` + `organizationAdminApi`). Type lấy từ {@code generated-schema}.
 *
 * Danh sách mộ KHÔNG có API chi tiết theo từng dòng: `GraveListRow` đã mang
 * `martyrLinkSummary` do backend tổng hợp (chống N+1), nên module này cố ý không
 * export helper kiểu `getEachGrave`/`getGraveFor…`/`…ByRow`.
 */
export type GraveListRow = components["schemas"]["GraveListRow"];
export type GraveListMartyrSummary = components["schemas"]["GraveListMartyrSummary"];
export type GravePageData = components["schemas"]["GravePageData"];
export type GravePageMeta = components["schemas"]["PageMeta"];

/** Trang kết quả search: `rows` từ `data` + `meta` phân trang từ envelope. */
export type GraveSearchPage = Readonly<{
  rows: GravePageData["rows"];
  meta: GravePageMeta | null;
}>;
/**
 * Hai kiểu dưới đây lấy THẲNG từ hợp đồng, không khai tay thêm trường nào.
 *
 * Bản trước bọc `Omit<…> & Readonly<{…}>` để nhét thêm 7 thuộc tính liệt sĩ
 * (`martyrCode`/`rankName`/`positionName`/`unitName`/`nativePlace`/`birthYear`/`sacrificeDate`)
 * mà hợp đồng, DTO backend lẫn câu SQL đều KHÔNG chở. Khai tay như vậy làm hai màn
 * (`/graves/:graveId` và rail GIS) biên dịch được và test vẫn xanh — vì fixture tự bịa response
 * mà backend không bao giờ phát — trong khi giao diện thật im lặng thiếu đúng những dòng đó.
 * Nay hợp đồng đã khai 7 trường nên không còn gì phải bọc.
 */
export type GraveData = components["schemas"]["GraveData"];
export type GraveMartyrLinkSummary = components["schemas"]["GraveMartyrLinkSummary"];
export type GraveCommandData = components["schemas"]["GraveCommandData"];
export type GraveMartyrLinkCommandData = components["schemas"]["GraveMartyrLinkCommandData"];
export type CreateGraveInput = components["schemas"]["CreateGraveRequest"];

/** Trạng thái vị trí — suy từ schema GraveData, không tự viết lại enum kỹ thuật. */
export type GravePositionState = NonNullable<components["schemas"]["GraveData"]["positionState"]>;

/** Loại mộ — suy từ schema GraveListRow. */
export type GraveTypeCode = components["schemas"]["GraveListRow"]["graveType"];

/**
 * Đầu vào tạo liên kết mộ–liệt sĩ do caller cung cấp: bộ chọn liệt sĩ, độ tin cậy,
 * ngày hiệu lực và tham chiếu bằng chứng. `linkType` bị loại khỏi type vì adapter
 * tự khóa cứng `SUSPECTED` (CR-S11-6-001 delta 4) — caller không thể truyền giá trị
 * khác, kể cả khi cố gắng nhồi thêm ở runtime.
 */
export type CreateGraveMartyrLinkInput = Omit<
  components["schemas"]["CreateGraveMartyrLinkRequest"],
  "linkType"
>;

/** Chỉ kết thúc liên kết theo ngày hiệu lực; không patch nội dung lịch sử. */
export type EndGraveMartyrLinkInput = components["schemas"]["EndGraveMartyrLinkRequest"];

/** Bộ lọc searchGraves — đúng whitelist transport của contract. */
export type GraveSearchFilters = Readonly<{
  page?: number;
  size?: number;
  q?: string;
  cemeteryId?: string;
  sectionId?: string;
  positionState?: GravePositionState;
  graveType?: GraveTypeCode;
  identityState?: string;
  physicalCondition?: string;
}>;

/** Bộ lọc export — cùng predicate, không phân trang (rowLimit do server). */
export type GraveExportFilters = Omit<GraveSearchFilters, "page" | "size">;

/** Loại liên kết S11 chỉ được phép tạo từ UI. */
const SUSPECTED_LINK_TYPE = "SUSPECTED";

const BASE_PATH = "/graves";

function signalInit(signal?: AbortSignal): RequestInit {
  return signal ? { signal } : {};
}

function commandHeaders(idempotencyKey: string): Record<string, string> {
  return { "Idempotency-Key": idempotencyKey, "X-Request-Id": createRequestId() };
}

function encodeQuery(entries: Array<[string, string]>): string {
  const query = new URLSearchParams();
  for (const [key, value] of entries) query.set(key, value);
  const encoded = query.toString();
  return encoded ? `?${encoded}` : "";
}

function filterEntries(filters: GraveSearchFilters): Array<[string, string]> {
  const entries: Array<[string, string]> = [];
  const pushText = (key: string, value: string | undefined): void => {
    if (value !== undefined && value !== "") entries.push([key, value]);
  };
  if (filters.page !== undefined) entries.push(["page", String(filters.page)]);
  if (filters.size !== undefined) entries.push(["size", String(filters.size)]);
  pushText("q", filters.q);
  pushText("cemeteryId", filters.cemeteryId);
  pushText("sectionId", filters.sectionId);
  pushText("positionState", filters.positionState);
  pushText("graveType", filters.graveType);
  pushText("identityState", filters.identityState);
  pushText("physicalCondition", filters.physicalCondition);
  return entries;
}

function postCommand<T>(
  path: string,
  body: unknown,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<T> {
  return apiRequest<T>(path, {
    method: "POST",
    headers: commandHeaders(idempotencyKey),
    body: JSON.stringify(body),
    ...signalInit(signal),
  });
}

async function toApiProblem(response: Response): Promise<ApiProblem | ApiContractError> {
  const headerRequestId = response.headers.get("X-Request-Id") ?? undefined;
  try {
    const body = (await response.json()) as ProblemDetails;
    if (body && typeof body.code === "string") {
      return new ApiProblem(response.status, body);
    }
  } catch {
    // fall through — body không phải problem+json
  }
  return new ApiContractError(
    `unexpected export response (HTTP ${response.status})`,
    response.status,
    headerRequestId,
  );
}

/**
 * {@code GET /graves} — trang mộ theo phạm vi dữ liệu người gọi
 * (operationId {@code searchGraves}). Mỗi dòng tự mang `martyrLinkSummary` đã mask
 * nên UI KHÔNG bao giờ gọi `getGrave` theo từng dòng.
 */
export async function searchGraves(
  filters: GraveSearchFilters,
  signal?: AbortSignal,
): Promise<GraveSearchPage> {
  const page = await apiRequestWithMeta<GravePageData, GravePageMeta>(
    `${BASE_PATH}${encodeQuery(filterEntries(filters))}`,
    signalInit(signal),
  );
  return { rows: page.data.rows, meta: page.meta };
}

/**
 * {@code GET /graves/export} — quyền riêng EXPORT_EXECUTE + audit server-side.
 * `apiRequest` chỉ parse envelope JSON nên đường CSV dùng fetch trực tiếp với cùng
 * header xác thực; lỗi chuẩn hóa thành {@code ApiProblem}.
 */
export async function exportGraves(
  filters: GraveExportFilters,
  signal?: AbortSignal,
): Promise<Blob> {
  const path = `${BASE_PATH}/export${encodeQuery(filterEntries(filters))}`;
  const baseUrl = import.meta.env.VITE_API_BASE_URL ?? "/api/v1";
  const authentication = getAuthenticationSession();
  const headers = new Headers({
    Accept: "text/csv, application/problem+json",
    Authorization: `Bearer ${await authentication.getAccessToken()}`,
    "X-Request-Id": createRequestId(),
  });
  const response = await fetch(`${baseUrl}${path}`, {
    method: "GET",
    headers,
    ...signalInit(signal),
  });
  if (!response.ok) {
    throw await toApiProblem(response);
  }
  return response.blob();
}

/**
 * {@code GET /graves/{graveId}} — projection chi tiết mộ, chỉ gọi khi người dùng mở
 * hồ sơ (operationId {@code getGrave}); không dùng cho danh sách.
 */
export function getGrave(graveId: string, signal?: AbortSignal): Promise<GraveData> {
  return apiRequest<GraveData>(`${BASE_PATH}/${encodeURIComponent(graveId)}`, signalInit(signal));
}

/**
 * {@code POST /cemeteries/{cemeteryId}/graves} — tạo hồ sơ mộ trong nghĩa trang;
 * lệnh ghi bắt buộc Idempotency-Key.
 */
export function createGrave(
  cemeteryId: string,
  input: CreateGraveInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<GraveCommandData> {
  return postCommand<GraveCommandData>(
    `/cemeteries/${encodeURIComponent(cemeteryId)}/graves`,
    input,
    idempotencyKey,
    signal,
  );
}

/**
 * {@code POST /graves/{graveId}/martyr-links} — tạo liên kết mộ–liệt sĩ.
 *
 * S11 chỉ cho phép candidate `SUSPECTED`: `linkType` KHÔNG nằm trong type đầu vào và
 * được adapter ghi đè cứng ở cuối object body, nên caller không thể tạo
 * Confirmed/Collective từ UI (nhánh đó thuộc workflow nội bộ S13/DNA-07).
 */
export function createGraveMartyrLink(
  graveId: string,
  input: CreateGraveMartyrLinkInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<GraveMartyrLinkCommandData> {
  return postCommand<GraveMartyrLinkCommandData>(
    `${BASE_PATH}/${encodeURIComponent(graveId)}/martyr-links`,
    { ...input, linkType: SUSPECTED_LINK_TYPE },
    idempotencyKey,
    signal,
  );
}

/**
 * {@code POST /grave-martyr-links/{linkId}/end} — kết thúc liên kết theo ngày hiệu lực
 * (operationId {@code endGraveMartyrLink}); không patch nội dung lịch sử.
 */
export function endGraveMartyrLink(
  linkId: string,
  input: EndGraveMartyrLinkInput,
  idempotencyKey: string,
  signal?: AbortSignal,
): Promise<GraveMartyrLinkCommandData> {
  return postCommand<GraveMartyrLinkCommandData>(
    `/grave-martyr-links/${encodeURIComponent(linkId)}/end`,
    input,
    idempotencyKey,
    signal,
  );
}
